import React, { useState } from 'react';
import { Note, UserSwitch, X, Lock, LockOpen, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import SubPickerDialog from './SubPickerDialog';

// One court's worth of slots. The parent owns the in-memory `slots` array and
// passes a slot-level patch fn (onSlotChange(slotId, patch)) for every edit.
//
// Lock Court Movement (MVP): when `weekNumber` is provided and the viewer can
// edit (not readOnly), the card surfaces a per-court "Lock movement for this
// court" toggle. The toggle PATCHes the backend immediately and reports the
// new state back to the parent via `onLockChange(courtNumber, locked)` so the
// finalize-preview summary stays in sync without a page refresh.
export default function CourtPointsCard({
  court,
  sessionId,
  weekNumber,
  courtTies = [],
  tiePicks = {},
  onTiePick,
  onSlotChange,
  onLockChange,
  readOnly = false,
}) {
  const [lockBusy, setLockBusy] = useState(false);
  const movementLocked = !!court.movement_locked;
  const canToggleLock = !readOnly && weekNumber !== undefined && weekNumber !== null;

  const toggleLock = async () => {
    if (lockBusy || !canToggleLock) return;
    const next = !movementLocked;
    setLockBusy(true);
    try {
      const r = await eventAPI.setCourtMovementLock(sessionId, weekNumber, court.court_number, next);
      const applied = !!r.data?.movement_locked;
      onLockChange?.(court.court_number, applied);
      toast.success(applied
        ? `Court ${court.court_number} movement locked for week ${weekNumber}.`
        : `Court ${court.court_number} movement unlocked.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' ? detail : 'Could not update movement lock.');
      toast.error(msg);
    } finally {
      setLockBusy(false);
    }
  };

  return (
    <section
      data-testid={`court-points-card-${court.court_number}`}
      className={`bg-[#fbfaf8] border rounded-2xl p-4 space-y-3 ${movementLocked ? 'border-amber-300' : 'border-[#e3dcd1]'}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Court</div>
          <div className="font-serif text-2xl text-[#013523]" data-testid={`court-number-${court.court_number}`}>
            {court.court_number}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="text-[11px] text-[#6a766e]">
            {court.slots.length} players
          </div>
          {/* Lock toggle — admin/EM only (gated by readOnly upstream). */}
          {canToggleLock ? (
            <label
              data-testid={`court-lock-toggle-${court.court_number}`}
              className={`inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none rounded-full border px-2 py-1 transition-colors ${
                movementLocked
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : 'bg-white border-[#e3dcd1] text-[#013523]'
              } ${lockBusy ? 'opacity-50 cursor-wait' : 'hover:bg-[#fbfaf8]'}`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={movementLocked}
                onChange={toggleLock}
                disabled={lockBusy}
                data-testid={`court-lock-checkbox-${court.court_number}`}
              />
              {movementLocked ? <Lock size={11} weight="fill" /> : <LockOpen size={11} />}
              <span>Lock movement for this court</span>
            </label>
          ) : movementLocked && (
            <span
              data-testid={`court-lock-readonly-${court.court_number}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-amber-50 text-amber-800 rounded-full px-2 py-0.5 border border-amber-300"
            >
              <Lock size={10} weight="fill" /> Movement locked
            </span>
          )}
        </div>
      </header>
      {movementLocked && canToggleLock && (
        <p
          data-testid={`court-lock-helper-${court.court_number}`}
          className="text-[11px] text-amber-800 bg-amber-50/60 border border-amber-200 rounded-lg px-2.5 py-1.5"
        >
          Players on this court will stay together next week. Neighboring courts will not exchange players with this court.
        </p>
      )}
      <div className="divide-y divide-[#e3dcd1]">
        {court.slots.map((slot) => {
          const tieButtons = (courtTies || [])
            .filter((t) => (t.tied_registration_ids || []).includes(slot.registration_id))
            .map((t) => {
              const key = `${court.court_number}-${t.role}`;
              return {
                key,
                role: t.role,
                direction: t.role === 'highest' ? 'up' : 'down',
                selected: tiePicks[key] === slot.registration_id,
                regId: slot.registration_id,
              };
            });
          return (
            <SlotRow
              key={slot.id}
              slot={slot}
              sessionId={sessionId}
              readOnly={readOnly}
              tieButtons={tieButtons}
              onTiePick={onTiePick}
              onChange={(patch) => onSlotChange(slot.id, patch)}
            />
          );
        })}
      </div>
    </section>
  );
}

function SlotRow({ slot, sessionId, onChange, readOnly, tieButtons = [], onTiePick }) {
  const [showSub, setShowSub] = useState(false);
  const [showNotes, setShowNotes] = useState(!!slot.notes);

  const setPoints = (v) => {
    if (v === '') { onChange({ points: null }); return; }
    const n = Math.max(0, Math.min(999, parseInt(v, 10) || 0));
    onChange({ points: n });
  };

  const toggleNoShow = (checked) => {
    onChange({
      no_show: checked,
      points: checked ? 0 : slot.points,
      is_substitute: checked ? false : slot.is_substitute,
      sub_profile_id: checked ? null : slot.sub_profile_id,
    });
  };

  const clearSub = () => {
    onChange({ is_substitute: false, sub_profile_id: null, sub_player_name: null });
  };

  return (
    <div className="py-2.5 first:pt-0 last:pb-0" data-testid={`slot-row-${slot.id}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate flex items-center gap-1.5" data-testid={`slot-name-${slot.id}`}>
            {slot.no_show ? (
              <span className="line-through text-[#6a766e]">{slot.player_name || '—'}</span>
            ) : (
              <span>{slot.player_name || '—'}</span>
            )}
            {slot.no_show && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700">No-show</span>
            )}
            {slot.is_substitute && slot.sub_player_name && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#c2a251]/20 text-[#7a611a]">
                Sub: {slot.sub_player_name}
              </span>
            )}
          </div>
          {slot.movement && (
            <div className="text-[10px] text-[#6a766e] uppercase tracking-wider">
              moved {slot.movement}
            </div>
          )}
        </div>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={999}
          placeholder="—"
          disabled={readOnly || slot.no_show}
          value={slot.points ?? ''}
          onChange={(e) => setPoints(e.target.value)}
          className="w-16 sm:w-20 px-2 py-1.5 rounded-lg border border-[#e3dcd1] bg-white text-right text-sm disabled:bg-[#f0ece3] disabled:text-[#6a766e]"
          data-testid={`slot-points-${slot.id}`}
        />
        {/* Reserved tie-button column — immediately RIGHT of the score field.
            Fixed width keeps the score column aligned on every row. */}
        <div className="w-[84px] shrink-0 flex flex-wrap justify-end gap-1" data-testid={`tie-controls-${slot.id}`}>
          {tieButtons.map((b) => {
            const base = 'px-2.5 py-2 rounded-lg text-xs font-bold flex items-center gap-1 justify-center transition-colors border';
            const cls = b.direction === 'up'
              ? (b.selected ? 'bg-[#015e3f] text-white border-[#015e3f] shadow-sm' : 'bg-white text-[#015e3f] border-[#015e3f]')
              : (b.selected ? 'bg-red-600 text-white border-red-600 shadow-sm' : 'bg-white text-red-600 border-red-500');
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => onTiePick?.(b.key, b.regId)}
                data-testid={`tie-pick-${b.role}-${b.regId}`}
                aria-pressed={b.selected}
                title={b.direction === 'up' ? 'Tap to move this player UP' : 'Tap to move this player DOWN'}
                className={`${base} ${cls}`}
              >
                {b.direction === 'up' ? <ArrowUp size={14} weight="bold" /> : <ArrowDown size={14} weight="bold" />}
                {b.direction === 'up' ? 'UP' : 'DOWN'}
              </button>
            );
          })}
        </div>
      </div>
      {!readOnly && (
        <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px]">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`slot-no-show-toggle-${slot.id}`}>
            <input
              type="checkbox"
              checked={!!slot.no_show}
              onChange={(e) => toggleNoShow(e.target.checked)}
              className="rounded border-[#e3dcd1]"
            />
            <span className="text-[#6a766e]">No-show</span>
          </label>
          {!slot.no_show && (
            <button
              type="button"
              onClick={() => slot.is_substitute ? clearSub() : setShowSub(true)}
              data-testid={`slot-sub-toggle-${slot.id}`}
              className="flex items-center gap-1 text-[#015e3f]"
            >
              <UserSwitch size={12} />
              {slot.is_substitute ? 'Remove sub' : 'Sub played'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowNotes((v) => !v)}
            data-testid={`slot-notes-toggle-${slot.id}`}
            className="flex items-center gap-1 text-[#6a766e]"
          >
            <Note size={12} />
            {slot.notes ? 'Edit note' : 'Add note'}
          </button>
        </div>
      )}
      {showNotes && (
        <textarea
          rows={2}
          disabled={readOnly}
          value={slot.notes || ''}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Optional notes…"
          data-testid={`slot-notes-input-${slot.id}`}
          className="mt-2 w-full px-2 py-1.5 rounded-lg border border-[#e3dcd1] bg-white text-xs disabled:bg-[#f0ece3]"
        />
      )}
      <SubPickerDialog
        open={showSub}
        sessionId={sessionId}
        onClose={() => setShowSub(false)}
        excludeProfileId={slot.profile_id}
        onPick={(p) => onChange({
          is_substitute: true,
          sub_profile_id: p.profile_id,
          sub_player_name: p.name,
        })}
      />
    </div>
  );
}
